import { formatXTick } from '@/pages/client-portal/ui/utils'
import { ProcessingTimeTrendChartTooltip } from '@/pages/ui/chart-tooltips/processing-time-trend-chart-tooltip/processing-time-trend-chart-tooltip'
import {
  LineChart,
  LineChartActiveMarker,
  LineChartCurrentMarker,
  LineChartDataLine,
  LineChartTickLines,
  LineChartTooltip,
  LineChartXAxis,
  LineChartYAxis,
} from '@/pages/ui/line-chart'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/card/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/tabs/tabs'

import { type ClientPortalProcessingTimeTrendItem } from '@/lib/queries/case-dashboard/types'
import { cn } from '@/lib/utils'

import {
  MIN_PLOT_AREA_WIDTH,
  PLOT_AREA_HEIGHT,
  PLOT_AREA_PADDING_RIGHT,
  PLOT_AREA_PADDING_TOP,
  PROCESSING_TIME_TREND_TIMEFRAME_OPTIONS,
  X_AXIS_HEIGHT,
  Y_AXIS_WIDTH,
} from './constants'
import { useProcessingTimeTrendCard } from './use-processing-time-trend-card'

export interface ProcessingTimeTrendCardProps {
  processingTimeTrendData?: ClientPortalProcessingTimeTrendItem[]
}

export const ProcessingTimeTrendCard = ({ processingTimeTrendData }: ProcessingTimeTrendCardProps) => {
  const { currentTimeFrame, processingTimeMonthlySeries, handleChangeTimeFrame } = useProcessingTimeTrendCard({
    processingTimeTrendData,
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className={cn('text-lg font-semibold text-black')}>USCIS Processing Time Trend</CardTitle>
      </CardHeader>

      <CardContent className={cn('flex flex-col-reverse gap-y-6 lg:flex-col')}>
        <Tabs value={currentTimeFrame} onValueChange={handleChangeTimeFrame}>
          <TabsList className={cn('w-full lg:w-fit')}>
            {PROCESSING_TIME_TREND_TIMEFRAME_OPTIONS.map(({ value, label }) => (
              <TabsTrigger key={value} value={String(value)} className={cn('w-full')}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Card>
          <CardContent className={cn('pb-9 pl-9 pr-0 pt-[60px]', 'overflow-x-auto')}>
            {processingTimeMonthlySeries && (
              <LineChart
                data={processingTimeMonthlySeries}
                dataKeys={['estimatedTime']}
                minPlotAreaWidth={MIN_PLOT_AREA_WIDTH}
                plotAreaHeight={PLOT_AREA_HEIGHT}
                plotAreaPadding={{
                  right: PLOT_AREA_PADDING_RIGHT,
                  top: PLOT_AREA_PADDING_TOP,
                }}
                xAxisHeight={X_AXIS_HEIGHT}
                yAxisWidth={Y_AXIS_WIDTH}
              >
                <LineChartXAxis dataKey="date" tickFormatter={formatXTick} />
                <LineChartYAxis title="#Months" />
                <LineChartTickLines />
                <LineChartDataLine dataKey="estimatedTime" />
                <LineChartCurrentMarker dataKey="estimatedTime" />
                <LineChartActiveMarker dataKey="estimatedTime" />
                <LineChartTooltip dataKeys={['estimatedTime']} render={() => <ProcessingTimeTrendChartTooltip />} />
              </LineChart>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
